/**
 * MasterTagField — wraps the shared TagField for the four new-study tags on
 * the Intake screen (Sponsor, CRO, Therapeutic area, Indication) with a
 * duplicate check and an "add as new" option (Jordan's decisions (264) and
 * (265), 2026-09-28; plan §7.5).
 *
 * Match first, add second (ORCHESTRATOR DECISIONS #3): leaving the field
 * runs POST /master/match for this field's listType; an exact or alias hit
 * fills the entry, the same as a pick. Only when the answer is 'unmatched'
 * for a non-empty normalized key does this wrapper offer
 * Add '<text>' as a new <noun>, which creates the entry through the
 * existing POST /master/:type and fills its id. That order is what keeps a
 * near-duplicate from being created when an existing entry would already
 * have matched — matching folds case, punctuation and (for companies)
 * legal suffixes while the create route's own duplicate check does not
 * (common.md hazard (v)).
 *
 * TagField.tsx itself is never edited (its "add it from the Admin tab
 * first" line stays Studies-only); this file is the one place Intake keeps
 * that string off the screen, by never leaving `state.candidates` at an
 * empty array (see the blur handler below).
 */
import { useRef, useState } from 'react';
import { TagField } from '../studies/TagField';
import type { TagFieldState } from '../studies/TagField';
import { matchMaster, createMasterEntry } from '../../api/client';
import type { MasterEntryRow, TagMatchCandidate } from '../../api/client';
import { secondaryBtnCls } from '../../../ui/styles';

export type IntakeTagListType = 'sponsor' | 'cro' | 'therapeutic_area' | 'indication';

const NOUN: Record<IntakeTagListType, string> = {
  sponsor: 'sponsor',
  cro: 'CRO',
  therapeutic_area: 'therapeutic area',
  indication: 'indication',
};

/** A pasted entry id (TagField invites one — see its placeholder). */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface CheckState {
  text: string;
  phase: 'checking' | 'unmatched' | 'error';
  message?: string;
}

interface Props {
  id: string;
  label: string;
  listType: IntakeTagListType;
  state: TagFieldState;
  onChange: (next: TagFieldState) => void;
}

export function MasterTagField({ id, label, listType, state, onChange }: Props) {
  const [check, setCheck] = useState<CheckState | null>(null);
  const [adding, setAdding] = useState(false);
  const [addNote, setAddNote] = useState<string | null>(null);
  const [addError, setAddError] = useState<string | null>(null);

  // Always the current props/state, for the async match/add callbacks below
  // to judge against — the render-time `state` they closed over goes stale
  // the moment the person types again while a request is in flight.
  const latest = useRef(state);
  latest.current = state;

  // Always the current onChange prop too, for the same reason `latest`
  // exists above: the four async continuations below (blur's matched and
  // unmatched fills, add's 201 and 409 fills) must call whatever write path
  // the host is using NOW, never the `onChange` this component closed over
  // at the render when the request started (PART J: "async onChange ...
  // never the render-time closure", one level above this wrapper — spreading
  // `latest.current` protects only this field's own text, not the rest of
  // the host's state the host's onChange writes into).
  const latestOnChange = useRef(onChange);
  latestOnChange.current = onChange;

  function handleTextChange(text: string) {
    onChange({ text, resolvedId: null, candidates: null });
    setCheck(null);
    setAddNote(null);
    setAddError(null);
  }

  function handlePick(candidate: TagMatchCandidate) {
    onChange({ text: candidate.entry.official_name, resolvedId: candidate.entry.id, candidates: null });
    setCheck(null);
  }

  function handleBlur() {
    const text = state.text.trim();
    if (text === '' || state.resolvedId || check?.text === text || UUID_RE.test(text)) return;
    setCheck({ text, phase: 'checking' });
    matchMaster(listType, text)
      .then((result) => {
        if (latest.current.text.trim() !== text || latest.current.resolvedId) return;
        if (result.status === 'matched') {
          latestOnChange.current({ text: result.entry.official_name, resolvedId: result.entry.id, candidates: null });
          setCheck(null);
        } else if (result.status === 'unmatched') {
          if (result.normalized === '') {
            setCheck({ text, phase: 'error', message: 'Nothing is left of that name to match on.' });
          } else {
            latestOnChange.current({ ...latest.current, candidates: result.candidates.length > 0 ? result.candidates : null });
            setCheck({ text, phase: 'unmatched' });
          }
        } else {
          setCheck(null);
        }
      })
      .catch((err) => {
        if (latest.current.text.trim() !== text || latest.current.resolvedId) return;
        const res = (err as { response?: { status?: number; data?: { error?: string } } })?.response;
        const message =
          res?.status === 409 && typeof res.data?.error === 'string' ? res.data.error : 'Could not check the master list.';
        setCheck({ text, phase: 'error', message });
      });
  }

  async function handleAdd() {
    if (!check) return;
    // Captured before the await (blocking fix): `check` is this closure's
    // own render-time snapshot, and the person may click a candidate button
    // or type something new while the create request is in flight.
    const text = check.text;
    setAdding(true);
    setAddError(null);
    try {
      const entry = await createMasterEntry(listType, { official_name: text });
      // PART B's drop rule, applied to the add answer the same way the blur
      // handler already applies it to the match answer above: dropped when
      // the field has moved on (typed something else, or already resolved
      // another way) while this request was in flight.
      if (latest.current.text.trim() !== text || latest.current.resolvedId) return;
      latestOnChange.current({ text: entry.official_name, resolvedId: entry.id, candidates: null });
      setCheck(null);
      setAddNote('Added to the master list as a new entry.');
    } catch (err) {
      if (latest.current.text.trim() !== text || latest.current.resolvedId) return;
      const res = (err as { response?: { status?: number; data?: { error?: string; entry?: MasterEntryRow } } })?.response;
      if (res?.status === 409 && res.data?.entry) {
        const entry = res.data.entry;
        latestOnChange.current({ text: entry.official_name, resolvedId: entry.id, candidates: null });
        setCheck(null);
        setAddNote('Already in the master list; that entry is filled in.');
      } else {
        setAddError(res?.data?.error ?? 'Could not add the entry.');
      }
    } finally {
      setAdding(false);
    }
  }

  const showCheck = Boolean(check) && check!.text === state.text.trim() && !state.resolvedId;

  return (
    <div onBlur={handleBlur}>
      <TagField id={id} label={label} state={state} onTextChange={handleTextChange} onPick={handlePick} />
      <div aria-live="polite">
        {showCheck && check!.phase === 'checking' && (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Checking the master list…</p>
        )}
        {showCheck && check!.phase === 'unmatched' && (
          <>
            {!state.candidates && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">No close matches in the master list.</p>}
            <button
              type="button"
              data-testid={`${id}-add`}
              className={`${secondaryBtnCls} mt-2`}
              disabled={adding}
              onClick={handleAdd}
            >
              {adding ? 'Adding…' : `Add '${check!.text}' as a new ${NOUN[listType]}`}
            </button>
          </>
        )}
        {showCheck && check!.phase === 'error' && (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">{check!.message}</p>
        )}
        {addError && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{addError}</p>}
        {state.resolvedId && addNote && <p className="text-xs text-primary-700 dark:text-primary-400">{addNote}</p>}
      </div>
    </div>
  );
}

export default MasterTagField;
